import React, { useState, useCallback } from 'react';
import { ArrowUpTrayIcon, CheckCircleIcon, ExclamationTriangleIcon, ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ingestionService } from '../../services/ingestionService';

interface FileItem {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
}

interface DocumentUploadProps {
  onUploadComplete?: (jobId: string, filename: string) => void;
}

export function DocumentUpload({ onUploadComplete }: DocumentUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [queue, setQueue] = useState<FileItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const addFiles = useCallback((files: File[]) => {
    const items: FileItem[] = files.map(file => {
      const validation = ingestionService.validateFile(file);
      return {
        id: crypto.randomUUID(),
        file,
        status: validation.valid ? 'pending' : 'error',
        error: validation.valid ? undefined : validation.error,
      };
    });
    setQueue(prev => [...prev, ...items]);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  }, [addFiles]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(Array.from(e.target.files));
      e.target.value = '';
    }
  }, [addFiles]);

  const removeFile = useCallback((id: string) => {
    setQueue(prev => prev.filter(f => f.id !== id));
  }, []);

  const processQueue = async () => {
    const pending = queue.filter(f => f.status === 'pending');
    if (pending.length === 0) return;

    setIsProcessing(true);
    setSuccessMessage(null);
    let successCount = 0;

    for (const item of pending) {
      setQueue(prev => prev.map(f => f.id === item.id ? { ...f, status: 'uploading' } : f));

      try {
        const uploadResponse = await ingestionService.uploadDocument(item.file);
        setQueue(prev => prev.filter(f => f.id !== item.id));
        onUploadComplete?.(uploadResponse.job_id, item.file.name);
        successCount++;
      } catch (err: any) {
        const msg = err?.response?.data?.detail || err?.message || 'Falha no upload';
        setQueue(prev => prev.map(f => f.id === item.id ? { ...f, status: 'error', error: msg } : f));
      }
    }

    setIsProcessing(false);

    if (successCount > 0) {
      const msg = successCount === 1
        ? 'Upload de 1 arquivo concluído com sucesso!'
        : `Upload de ${successCount} arquivos concluído com sucesso!`;
      setSuccessMessage(msg);
      setTimeout(() => setSuccessMessage(null), 5000);
    }
  };

  const pendingCount = queue.filter(f => f.status === 'pending').length;

  return (
    <div
      className={`bg-white shadow rounded-lg p-6 border-2 border-dashed transition-colors ${isDragging ? 'border-emerald-500 bg-emerald-50' : 'border-transparent'
        }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="flex items-center gap-2 mb-4">
        <ArrowUpTrayIcon className="h-5 w-5 text-gray-500" />
        <h3 className="text-lg font-medium text-gray-900">Upload de Documentos</h3>
      </div>

      <div className="space-y-4">
        <p className="text-sm text-gray-500">
          Arraste e solte arquivos PDF, DOCX ou TXT aqui ou clique em <strong>browse...</strong> para selecionar. Múltiplos arquivos permitidos.
        </p>

        <label className="block">
          <span className="sr-only">Escolher arquivos</span>
          <input
            type="file"
            multiple
            accept=".pdf,.txt,.docx"
            onChange={handleFileSelect}
            disabled={isProcessing}
            className="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-md file:border-0
              file:text-sm file:font-semibold
              file:bg-emerald-50 file:text-emerald-700
              hover:file:bg-emerald-100
              disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </label>

        {/* File Queue */}
        {queue.length > 0 && (
          <div className="space-y-2">
            {queue.map(item => (
              <div key={item.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-md">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-700 truncate">{item.file.name}</p>
                  {item.error && <p className="text-xs text-red-500 mt-0.5">{item.error}</p>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {item.status === 'pending' && (
                    <span className="text-xs text-gray-400">Pendente</span>
                  )}
                  {item.status === 'uploading' && (
                    <ArrowPathIcon className="h-4 w-4 text-emerald-500 animate-spin" />
                  )}
                  {item.status === 'done' && (
                    <CheckCircleIcon className="h-4 w-4 text-green-500" />
                  )}
                  {item.status === 'error' && (
                    <ExclamationTriangleIcon className="h-4 w-4 text-red-400" />
                  )}
                  {item.status !== 'uploading' && (
                    <button
                      onClick={() => removeFile(item.id)}
                      className="text-gray-400 hover:text-gray-600"
                      title="Remover"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {successMessage && (
          <div className="rounded-md bg-green-50 p-3 flex items-center gap-2">
            <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
            <p className="text-sm text-green-700">{successMessage}</p>
          </div>
        )}

        {pendingCount > 0 && (
          <button
            onClick={processQueue}
            disabled={isProcessing}
            className="inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? (
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowUpTrayIcon className="h-4 w-4" />
            )}
            {isProcessing
              ? 'Enviando...'
              : `Enviar ${pendingCount} arquivo${pendingCount > 1 ? 's' : ''}`}
          </button>
        )}
      </div>
    </div>
  );
}
